// Chemin: C:\gestion-copro\src\pages\coproprietaire\DashboardCoproprietairePage.tsx
// Dashboard de l'espace copropriétaire
// Session 56 : création initiale
// Session 72 : Ajout section "Mes lots" avec workflow réclamation
// Session 72 FIX : Correction récupération lots + email CS + texte attestation

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  PlusCircle,
  FileText,
  Clock,
  CheckCircle,
  Loader2,
  Building2,
  Calendar,
  ArrowRight,
  ShieldCheck,
  Home,
  Car,
  Archive,
  Bike,
  Mail,
  Info
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { collection, query, where, orderBy, limit, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getLotsByProprietaire, getLotsRattaches } from '../../services/lot.service';
import { Lot, TypeLot, TYPE_LOT_LABELS } from '../../types/lot.types';

interface SignalementResume {
  id: string;
  titre: string;
  statut: string;
  createdAt: Timestamp;
}

// Icône selon le type de lot
const getLotIcon = (type: TypeLot) => {
  switch (type) {
    case 'appartement':
    case 'studio':
      return <Home className="w-4 h-4" />;
    case 'parking':
    case 'parking_couvert':
    case 'parking_exterieur':
      return <Car className="w-4 h-4" />;
    case 'cave':
      return <Archive className="w-4 h-4" />;
    case 'local_velo':
      return <Bike className="w-4 h-4" />;
    case 'local_commercial':
    case 'bureau':
      return <Building2 className="w-4 h-4" />;
    default:
      return <Home className="w-4 h-4" />;
  }
};

// Vérifie si un lot est principal
const isLotPrincipal = (type: TypeLot): boolean => {
  return ['appartement', 'studio', 'local_commercial', 'bureau'].includes(type);
};

const DashboardCoproprietairePage: React.FC = () => {
  const { firebaseUser, userProfile } = useAuth();
  const { copropriete } = useCopropriete();
  
  const [mesSignalements, setMesSignalements] = useState<SignalementResume[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    enCours: 0,
    resolus: 0
  });

  // Session 72 : État pour les lots
  const [mesLots, setMesLots] = useState<Lot[]>([]);
  const [lotsRattachesMap, setLotsRattachesMap] = useState<Record<string, Lot[]>>({});
  const [loadingLots, setLoadingLots] = useState(true);
  
  // Session 72 FIX : Email du CS
  const [emailCS, setEmailCS] = useState<string | null>(null);

  // Charger les signalements de l'utilisateur
  useEffect(() => {
    const fetchMesSignalements = async () => {
      if (!firebaseUser?.uid || !copropriete?.id) {
        setLoading(false);
        return;
      }

      try {
        const signalementsRef = collection(db, 'signalements');
        const q = query(
          signalementsRef,
          where('coproprieteId', '==', copropriete.id),
          where('userId', '==', firebaseUser.uid),
          orderBy('createdAt', 'desc'),
          limit(5)
        );

        const snapshot = await getDocs(q);
        const signalements: SignalementResume[] = [];
        let enCours = 0;
        let resolus = 0;

        snapshot.forEach((doc) => {
          const data = doc.data();
          signalements.push({
            id: doc.id,
            titre: data.titre || 'Sans titre',
            statut: data.statut || 'nouveau',
            createdAt: data.createdAt
          });

          if (data.statut === 'resolu' || data.statut === 'clos') {
            resolus++;
          } else {
            enCours++;
          }
        });

        setMesSignalements(signalements);
        setStats({
          total: snapshot.size,
          enCours,
          resolus
        });
      } catch (error) {
        console.error('Erreur chargement signalements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMesSignalements();
  }, [firebaseUser?.uid, copropriete?.id]);

  // Session 72 : Charger les lots du copropriétaire
  useEffect(() => {
    const fetchMesLots = async () => {
      if (!firebaseUser?.uid || !copropriete?.id) {
        setLoadingLots(false);
        return;
      }

      try {
        setLoadingLots(true);
        const lots = await getLotsByProprietaire(copropriete.id, firebaseUser.uid);
        
        // Séparer lots principaux et construire la map des rattachés
        const lotsPrincipaux = lots.filter(lot => isLotPrincipal(lot.type) && !lot.lotPrincipalId);
        const rattachesMap: Record<string, Lot[]> = {};
        
        for (const lotPrincipal of lotsPrincipaux) {
          const rattaches = await getLotsRattaches(lotPrincipal.id);
          if (rattaches.length > 0) {
            rattachesMap[lotPrincipal.id] = rattaches;
          }
        }
        
        setMesLots(lotsPrincipaux);
        setLotsRattachesMap(rattachesMap);
      } catch (error) {
        console.error('Erreur chargement lots:', error);
      } finally {
        setLoadingLots(false);
      }
    };

    fetchMesLots();
  }, [firebaseUser?.uid, copropriete?.id]);

  // Session 72 FIX : Charger l'email du CS depuis les tokens Gmail
  useEffect(() => {
    const fetchEmailCS = async () => {
      if (!copropriete?.id) return;
      
      try {
        // L'email Gmail est stocké dans la sous-collection gmailTokens
        const tokensRef = doc(db, 'coproprietes', copropriete.id, 'gmailTokens', 'current');
        const tokensSnap = await getDoc(tokensRef);
        
        if (tokensSnap.exists()) {
          const data = tokensSnap.data();
          if (data.email) {
            setEmailCS(data.email);
          }
        }
      } catch (error) {
        console.error('Erreur récupération email CS:', error);
      }
    };

    fetchEmailCS();
  }, [copropriete?.id]);

  // Formater la date
  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Badge statut
  const getStatutBadge = (statut: string) => {
    const configs: Record<string, { bg: string; text: string; label: string }> = {
      nouveau: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Nouveau' },
      en_cours: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'En cours' },
      transmis: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Transmis' },
      resolu: { bg: 'bg-green-100', text: 'text-green-800', label: 'Résolu' },
      clos: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Clos' }
    };
    const config = configs[statut] || configs.nouveau;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Salutation selon l'heure
  const getSalutation = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  // Calculer total tantièmes
  const getTotalTantiemes = (): number => {
    let total = 0;
    for (const lot of mesLots) {
      total += lot.tantièmesGeneraux;
      const rattaches = lotsRattachesMap[lot.id] || [];
      for (const rattache of rattaches) {
        total += rattache.tantièmesGeneraux;
      }
    }
    return total;
  };

  return (
    <div className="space-y-6">
      {/* En-tête de bienvenue */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getSalutation()}, {userProfile?.prenom || 'cher copropriétaire'} !
            </h1>
            <p className="text-gray-600 mt-1">
              Bienvenue dans votre espace copropriétaire
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </div>
        </div>

        {/* Info copropriété */}
        {copropriete && (
          <div className="mt-4 flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
            <Building2 className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-900">{copropriete.nom}</p>
              <p className="text-sm text-green-700">
                {copropriete.adresse?.rue}, {copropriete.adresse?.codePostal} {copropriete.adresse?.ville}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Session 72 : Section Mes lots */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Home className="w-5 h-5 text-gray-600" />
            <h2 className="font-semibold text-gray-900">Mes lots</h2>
          </div>
          {mesLots.length > 0 && (
            <span className="text-sm text-gray-500">
              {getTotalTantiemes()} tantièmes
            </span>
          )}
        </div>

        {loadingLots ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : mesLots.length === 0 ? (
          /* Aucun lot attribué - Message d'instruction */
          <div className="p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-amber-900">
                    Aucun lot ne vous est encore attribué
                  </h3>
                  <p className="text-sm text-amber-700 mt-2">
                    Pour associer vos lots à votre compte, merci d'envoyer un email au Conseil Syndical :
                  </p>
                  
                  {emailCS ? (
                    <a 
                      href={`mailto:${emailCS}?subject=Demande d'attribution de lots - ${userProfile?.prenom || ''} ${userProfile?.nom || ''}`}
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {emailCS}
                    </a>
                  ) : (
                    <p className="text-sm text-amber-800 mt-2 font-medium">
                      Contactez le Conseil Syndical de votre copropriété.
                    </p>
                  )}

                  <div className="mt-4 p-3 bg-white rounded border border-amber-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">Contenu à fournir :</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500">•</span>
                        Vos numéros de lots (appartement, parking, cave...)
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500">•</span>
                        En pièce jointe : attestation de signature notariale sans prix de vente, indiquant clairement tous vos numéros de lots (pas l'acte complet de vente)
                      </li>
                    </ul>
                  </div>

                  <p className="text-xs text-amber-600 mt-3">
                    Le Conseil Syndical validera votre demande sous 48h.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Affichage des lots */
          <div className="divide-y divide-gray-100">
            {mesLots.map((lot) => {
              const rattaches = lotsRattachesMap[lot.id] || [];
              const totalTantiemesLot = lot.tantièmesGeneraux + rattaches.reduce((sum, r) => sum + r.tantièmesGeneraux, 0);
              
              return (
                <div key={lot.id} className="p-4">
                  {/* Lot principal */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                        {getLotIcon(lot.type)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Lot {lot.numero} — {TYPE_LOT_LABELS[lot.type]}
                        </p>
                        <p className="text-sm text-gray-500">
                          {lot.etage && `${lot.etage}`}
                          {lot.etage && lot.batiment && ' • '}
                          {lot.batiment && `Bât. ${lot.batiment}`}
                          {!lot.etage && !lot.batiment && 'Emplacement non renseigné'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{totalTantiemesLot} ‰</p>
                      {rattaches.length > 0 && (
                        <p className="text-xs text-gray-500">
                          dont {rattaches.length} lot{rattaches.length > 1 ? 's' : ''} rattaché{rattaches.length > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Lots rattachés */}
                  {rattaches.length > 0 && (
                    <div className="mt-3 ml-13 pl-4 border-l-2 border-gray-200 space-y-2">
                      {rattaches.map((rattache) => (
                        <div key={rattache.id} className="flex items-center justify-between py-1">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400">
                              {getLotIcon(rattache.type)}
                            </span>
                            <span className="text-sm text-gray-600">
                              Lot {rattache.numero} — {TYPE_LOT_LABELS[rattache.type]}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {rattache.tantièmesGeneraux} ‰
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tuiles d'actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Nouveau signalement */}
        <Link
          to="/espace-copro/signaler"
          className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-green-300 hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <PlusCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                Nouveau signalement
              </h3>
              <p className="text-sm text-gray-500">
                Signaler un problème
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
          </div>
        </Link>

        {/* Mes signalements */}
        <Link
          to="/espace-copro/mes-signalements"
          className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-200 transition-colors">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-orange-700 transition-colors">
                Mes signalements
              </h3>
              <p className="text-sm text-gray-500">
                {stats.total} signalement{stats.total > 1 ? 's' : ''}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
          </div>
        </Link>

        {/* Documents */}
        <Link
          to="/espace-copro/documents"
          className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                Documents
              </h3>
              <p className="text-sm text-gray-500">
                Accéder aux documents
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
          </div>
        </Link>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total signalements</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.enCours}</p>
              <p className="text-sm text-gray-500">En cours</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.resolus}</p>
              <p className="text-sm text-gray-500">Résolus</p>
            </div>
          </div>
        </div>
      </div>

      {/* Derniers signalements */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Mes derniers signalements</h2>
          {mesSignalements.length > 0 && (
            <Link
              to="/espace-copro/mes-signalements"
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              Voir tout
            </Link>
          )}
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : mesSignalements.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 mb-4">Vous n'avez pas encore fait de signalement</p>
            <Link
              to="/espace-copro/signaler"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <PlusCircle className="w-4 h-4" />
              Faire un signalement
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {mesSignalements.map((signalement) => (
              <Link
                key={signalement.id}
                to={`/espace-copro/mes-signalements/${signalement.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {signalement.titre}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDate(signalement.createdAt)}
                  </p>
                </div>
                <div className="ml-4">
                  {getStatutBadge(signalement.statut)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Encart informatif */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-900">Le conseil syndical est à votre écoute</h3>
            <p className="text-sm text-green-700 mt-1">
              Signalez tout problème touchant les parties communes de votre résidence. 
              Le conseil syndical examine chaque signalement et assure le suivi auprès du syndic et des prestataires.
            </p>
            <p className="text-xs text-green-600 mt-2">
              Rappel : Les problèmes privatifs (intérieur de votre appartement) doivent être traités directement avec le syndic.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCoproprietairePage;
